import React from "react";
import { graphql, PageProps, Link } from "gatsby";
import {
  Shield,
  FileCheck,
  CheckCircle,
  Clock,
  Users,
  DollarSign,
} from "lucide-react";
import Footer from "@/components/Footer";
import Header from "@/components/Header";
import SEO from "@/components/SEO";
import ButtonCTA from "@/components/ButtonCTA";
import Breadcrumb from "@/components/Breadcrumb";
import BenefitCard from "@/components/regions/BenefitCard";
import StatCard from "@/components/regions/StatCard";
import ContentCard from "@/components/regions/ContentCard";
import ComparisonSection from "@/components/regions/ComparisonSection";
import ResponsiveHeading from "@/components/regions/ResponsiveHeading";
import { RegionData } from "./types";
import { CRJMC_PRICE } from "@/constants";

interface RegionTemplateProps extends PageProps {
  regionData: RegionData;
}

const RegionTemplate: React.FC<RegionTemplateProps> = ({
  data,
  regionData,
}) => {
  const benefits = [
    {
      icon: Clock,
      title: "15-Minute CRJMC Results",
      description: `Get your background check results in just 15 minutes - no waiting weeks for ${regionData.name} police station processing.`,
    },
    {
      icon: Shield,
      title: "Approved third-party provider",
      description:
        "Our CRJMC checks are fully accepted by the Ministry of the Solicitor General for security guard application in Ontario.",
    },
    {
      icon: FileCheck,
      title: "Digital Format Ready",
      description:
        "Receive your CRJMC in digital PDF format, ready for immediate online license application or renewal.",
    },
    {
      icon: CheckCircle,
      title: "Complete Background Check",
      description:
        "Comprehensive verification including criminal record, outstanding charges, and judicial matters - everything needed for your security license.",
    },
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />

      <main>
        {/* Hero Section */}
        <section
          className="relative py-10 px-4 bg-gradient-to-br from-primary to-primary-dark text-white overflow-hidden"
          aria-labelledby="hero-heading"
        >
          <div className="absolute inset-0 z-0 bg-black/80">
            <picture>
              <source
                srcSet="/images/security-guard/security-guard-with-background-check-cleared-big.jpg"
                type="image/jpeg"
              />
              <img
                src="/images/security-guard/security-guard-with-background-check-cleared-big.jpg"
                alt={`Fast ${regionData.name} Police Check and Criminal Record & Judicial Matters Check Online`}
                className="w-full h-full object-cover opacity-30"
                width="1920"
                height="1080"
              />
            </picture>
          </div>
          <div className="container mx-auto max-w-6xl relative z-10">
            <Breadcrumb
              items={[
                { label: "Ontario", href: "/ontario" },
                { label: `${regionData.name} Police Check` },
              ]}
              variant="dark"
              textColor="text-gray-400"
              linkColor="hover:text-emerald-400"
            />
            <div className="text-center mt-10">
              <ResponsiveHeading
                as="h1"
                size="2xl"
                centered
                className="drop-shadow-sm"
                id="hero-heading"
              >
                {regionData.name} Police Check
                <br />
                Criminal Record & Judicial Matters Check (CRJMC) Online <br />
                Results in 15 Minutes
              </ResponsiveHeading>
              <p className="text-base sm:text-lg md:text-xl mb-8 max-w-2xl mx-auto drop-shadow-sm">
                Fast online {regionData.name} CRJMC and police check service.
                Faster than {regionData.name} Police Service, accepted for
                employment, licensing, and volunteering.
              </p>
              <div className="flex flex-col items-center gap-3">
                <ButtonCTA />
              </div>
            </div>
          </div>
        </section>

        {/* Quick Process Overview */}
        <section className="py-8 px-8 bg-white">
          <div className="container mx-auto max-w-5xl">
            <div className="grid md:grid-cols-4 gap-6">
              <div className="p-4 rounded-lg bg-gradient-to-br from-white to-primary/5 shadow-sm hover:shadow-md transition-all duration-300">
                <div className="flex items-center gap-3">
                  <div className="flex items-center justify-center w-10 h-10 rounded-full bg-primary/10">
                    <svg
                      className="w-5 h-5 text-primary"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
                      />
                    </svg>
                  </div>
                  <p className="text-sm font-medium text-gray-800">
                    Apply Online
                    <span className="block text-gray-500 mt-1 text-xs">
                      Takes 5 minutes
                    </span>
                  </p>
                </div>
              </div>
              <div className="p-4 rounded-lg bg-gradient-to-br from-white to-primary/5 shadow-sm hover:shadow-md transition-all duration-300">
                <div className="flex items-center gap-3">
                  <div className="flex items-center justify-center w-10 h-10 rounded-full bg-primary/10">
                    <svg
                      className="w-5 h-5 text-primary"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z"
                      />
                    </svg>
                  </div>
                  <p className="text-sm font-medium text-gray-800">
                    Verify Identity
                    <span className="block text-gray-500 mt-1 text-xs">
                      Secure & Quick
                    </span>
                  </p>
                </div>
              </div>
              <div className="p-4 rounded-lg bg-gradient-to-br from-white to-primary/5 shadow-sm hover:shadow-md transition-all duration-300">
                <div className="flex items-center gap-3">
                  <div className="flex items-center justify-center w-10 h-10 rounded-full bg-primary/10">
                    <svg
                      className="w-5 h-5 text-primary"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                      />
                    </svg>
                  </div>
                  <p className="text-sm font-medium text-gray-800">
                    Get Results
                    <span className="block text-gray-500 mt-1 text-xs">
                      In 15 minutes
                    </span>
                  </p>
                </div>
              </div>
              <div className="p-4 rounded-lg bg-gradient-to-br from-white to-primary/5 shadow-sm hover:shadow-md transition-all duration-300">
                <div className="flex items-center gap-3">
                  <div className="flex items-center justify-center w-10 h-10 rounded-full bg-primary/10">
                    <svg
                      className="w-5 h-5 text-primary"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M5 13l4 4L19 7"
                      />
                    </svg>
                  </div>
                  <p className="text-sm font-medium text-gray-800">
                    Submit for License
                    <span className="block text-gray-500 mt-1 text-xs">
                      Ready to use
                    </span>
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* CRJMC Overview Section */}
        <section className="py-16 px-4 bg-gradient-to-br from-white to-gray-50">
          <div className="container mx-auto max-w-6xl">
            <ResponsiveHeading
              centered
              size="xl"
              subtext="Understanding the Criminal Record and Judicial Matters Check (CRJMC) requirement for security positions."
            >
              What is a CRJMC Check?
            </ResponsiveHeading>
            <div className="mt-8 grid md:grid-cols-2 gap-8">
              <div className="bg-white rounded-lg p-8 shadow-md">
                <h3 className="text-xl font-bold mb-4">CRJMC Coverage</h3>
                <p className="text-gray-600 mb-4">
                  A <strong>CRJMC Background Check</strong> is a comprehensive
                  screening that includes:
                </p>
                <ul className="space-y-3">
                  <li className="flex items-start">
                    <CheckCircle className="w-5 h-5 text-primary mt-1 mr-3 flex-shrink-0" />
                    <span>Criminal convictions and findings of guilt</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="w-5 h-5 text-primary mt-1 mr-3 flex-shrink-0" />
                    <span>Outstanding charges and warrants</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="w-5 h-5 text-primary mt-1 mr-3 flex-shrink-0" />
                    <span>Judicial orders and court restrictions</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="w-5 h-5 text-primary mt-1 mr-3 flex-shrink-0" />
                    <span>Findings under the Youth Criminal Justice Act</span>
                  </li>
                </ul>
              </div>
              <div className="bg-white rounded-lg p-8 shadow-md">
                <h3 className="text-xl font-bold mb-4">Key Information</h3>
                <ul className="space-y-3">
                  <li className="flex items-start">
                    <CheckCircle className="w-5 h-5 text-primary mt-1 mr-3 flex-shrink-0" />
                    <span>
                      <strong>Mandatory Requirement:</strong> As of February
                      2024, CRJMC is required for all Ontario security guard
                      license applications
                    </span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="w-5 h-5 text-primary mt-1 mr-3 flex-shrink-0" />
                    <span>
                      <strong>Universal Format:</strong> The same CRJMC document
                      is valid for all purposes and professions
                    </span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="w-5 h-5 text-primary mt-1 mr-3 flex-shrink-0" />
                    <span>
                      <strong>Validity:</strong> Results are valid for 6 months
                      from the date of issue
                    </span>
                  </li>
                </ul>
              </div>
            </div>
            <div className="mt-8 bg-blue-50 border border-blue-200 rounded-lg p-6">
              <div className="flex items-start gap-3">
                <svg
                  className="w-6 h-6 text-blue-500 mt-1 flex-shrink-0"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                </svg>
                <div>
                  <h4 className="font-semibold text-blue-900 mb-1">
                    Need More Information?
                  </h4>
                  <p className="text-blue-800">
                    For detailed information about eligibility, requirements,
                    and the application process, read our complete guide on{" "}
                    <Link
                      to="/post/getting-a-crjmc-in-ontario-a-complete-guide-2025/"
                      className="text-blue-600 hover:text-blue-800 underline font-medium"
                    >
                      CRJMC in Ontario
                    </Link>{" "}
                    (updated 2025)
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Benefits Section */}
        <section className="py-16 px-4" aria-labelledby="benefits-heading">
          <div className="container mx-auto max-w-6xl">
            <ResponsiveHeading centered size="xl" id="benefits-heading">
              Why Choose Our CRJMC Service
            </ResponsiveHeading>
            <div
              className="grid grid-cols-1 md:grid-cols-2 gap-8 mt-12"
              role="list"
            >
              {benefits.map((benefit, index) => (
                <BenefitCard key={index} {...benefit} role="listitem" />
              ))}
            </div>
          </div>
        </section>

        {/* First CTA */}
        <section className="py-8 px-4 bg-primary">
          <div className="container mx-auto max-w-4xl text-center text-white">
            <ResponsiveHeading
              size="lg"
              centered
              withHighlight={false}
              className="text-white"
            >
              Need Your CRJMC Today?
            </ResponsiveHeading>
            <p className="text-base sm:text-lg md:text-xl mb-8">
              Don't wait weeks for a police check. Get your results in 15
              minutes.
            </p>
            <ButtonCTA showSubheader={false} />
          </div>
        </section>

        {/* Online vs Police Station Comparison */}
        <ComparisonSection
          policeStationName={`${regionData.name} Police Service`}
          policeStationLink={regionData.policeServiceUrl}
        />

        {/* Understanding CRJMC Section */}
        <section className="py-16 px-4">
          <div className="container mx-auto max-w-6xl">
            <ResponsiveHeading centered size="xl">
              Online CRJMC with{" "}
              <Link
                to="/"
                className="text-primary hover:text-primary-dark underline"
              >
                CRJMC.net
              </Link>
              : What's Included
            </ResponsiveHeading>
            <div className="bg-white rounded-lg p-8 shadow-md">
              <div className="grid md:grid-cols-2 gap-8">
                <div>
                  <h3 className="text-xl font-bold mb-4">
                    Background Check Coverage
                  </h3>
                  <ul className="space-y-3">
                    <li className="flex items-start">
                      <CheckCircle className="w-5 h-5 text-primary mt-1 mr-3 flex-shrink-0" />
                      <span>Criminal convictions</span>
                    </li>
                    <li className="flex items-start">
                      <CheckCircle className="w-5 h-5 text-primary mt-1 mr-3 flex-shrink-0" />
                      <span>Outstanding charges and warrants</span>
                    </li>
                    <li className="flex items-start">
                      <CheckCircle className="w-5 h-5 text-primary mt-1 mr-3 flex-shrink-0" />
                      <span>Judicial orders</span>
                    </li>
                    <li className="flex items-start">
                      <CheckCircle className="w-5 h-5 text-primary mt-1 mr-3 flex-shrink-0" />
                      <span>Absolute and conditional discharges</span>
                    </li>
                  </ul>
                </div>
                <div>
                  <h3 className="text-xl font-bold mb-4">Key Features</h3>
                  <ul className="space-y-3">
                    <li className="flex items-start">
                      <CheckCircle className="w-5 h-5 text-primary mt-1 mr-3 flex-shrink-0" />
                      <span>Officially approved format</span>
                    </li>
                    <li className="flex items-start">
                      <CheckCircle className="w-5 h-5 text-primary mt-1 mr-3 flex-shrink-0" />
                      <span>Valid for 6 months</span>
                    </li>
                    <li className="flex items-start">
                      <CheckCircle className="w-5 h-5 text-primary mt-1 mr-3 flex-shrink-0" />
                      <span>Digital delivery</span>
                    </li>
                    <li className="flex items-start">
                      <CheckCircle className="w-5 h-5 text-primary mt-1 mr-3 flex-shrink-0" />
                      <span>Secure PDF format</span>
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* License Application Process */}
        <section className="py-16 px-4 bg-gray-50">
          <div className="container mx-auto max-w-6xl">
            <ResponsiveHeading centered size="xl">
              Using Your CRJMC for <br className="hidden sm:block" />
              {regionData.name} Security Guard Licensing
            </ResponsiveHeading>
            <div className="grid md:grid-cols-2 gap-8">
              <div className="bg-white rounded-lg p-8 shadow-md">
                <h3 className="text-xl font-bold mb-4">
                  New License Application
                </h3>
                <div className="flex flex-col gap-4">
                  <ul className="space-y-3">
                    <li className="flex items-center">
                      <CheckCircle className="w-5 h-5 text-primary mr-3 flex-shrink-0" />
                      <span>
                        Get your <strong>CRJMC</strong> (15 minutes)
                      </span>
                    </li>
                    <li className="flex items-center">
                      <CheckCircle className="w-5 h-5 text-primary mr-3 flex-shrink-0" />
                      <span>
                        Complete <strong>40-hour training course</strong>
                      </span>
                    </li>
                    <li className="flex items-center">
                      <CheckCircle className="w-5 h-5 text-primary mr-3 flex-shrink-0" />
                      <span>
                        Pass <strong>Ministry exam</strong>
                      </span>
                    </li>
                    <li className="flex items-center">
                      <CheckCircle className="w-5 h-5 text-primary mr-3 flex-shrink-0" />
                      <span>
                        Submit <strong>license application</strong> with CRJMC
                      </span>
                    </li>
                  </ul>
                  <div className="mt-6 p-4 bg-primary/5 rounded-lg">
                    <p className="text-sm">
                      <strong>Total Cost:</strong> Approximately{" "}
                      <strong>$355</strong> (including training, CRJMC, exam,
                      and license fees)
                    </p>
                  </div>
                </div>
              </div>
              <div className="bg-white rounded-lg p-8 shadow-md">
                <h3 className="text-xl font-bold mb-4">CRJMC Renewal</h3>
                <div className="flex flex-col gap-4">
                  <ul className="space-y-3">
                    <li className="flex items-center">
                      <CheckCircle className="w-5 h-5 text-primary mr-3 flex-shrink-0" />
                      <span>
                        Get new <strong>CRJMC</strong> (
                        <Link
                          to="/renewal-discount"
                          className="text-primary hover:text-primary-dark underline"
                        >
                          <strong>20% off</strong> for renewals
                        </Link>
                        )
                      </span>
                    </li>
                    <li className="flex items-center">
                      <CheckCircle className="w-5 h-5 text-primary mr-3 flex-shrink-0" />
                      <span>
                        Submit <strong>renewal application</strong>
                      </span>
                    </li>
                    <li className="flex items-center">
                      <CheckCircle className="w-5 h-5 text-primary mr-3 flex-shrink-0" />
                      <span>
                        Pay <strong>$80</strong> renewal fee
                      </span>
                    </li>
                    <li className="flex items-center">
                      <CheckCircle className="w-5 h-5 text-primary mr-3 flex-shrink-0" />
                      <span>
                        Receive <strong>digital license</strong> immediately
                      </span>
                    </li>
                  </ul>
                  <div className="mt-6 p-4 bg-primary/5 rounded-lg">
                    <p className="text-sm">
                      <strong>Pro Tip:</strong> Start renewal up to{" "}
                      <strong>90 days</strong> before expiry
                    </p>
                  </div>
                </div>
              </div>
            </div>
            <div className="mt-8 bg-blue-50 border border-blue-200 rounded-lg p-6">
              <div className="flex items-start gap-3">
                <svg
                  className="w-6 h-6 text-blue-500 mt-1 flex-shrink-0"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                </svg>
                <div>
                  <h4 className="font-semibold text-blue-900 mb-1">
                    Need More Information?
                  </h4>
                  <p className="text-blue-800">
                    For a detailed walkthrough of the licensing process,
                    requirements, and insider tips, read our complete guide on{" "}
                    <Link
                      to="/post/complete-guide-to-ontario-security-license/"
                      className="text-blue-600 hover:text-blue-800 underline font-medium"
                    >
                      Ontario Security Guard License
                    </Link>{" "}
                    (updated 2025)
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Region Security Market Overview */}
        <section className="py-8 px-4 bg-gradient-to-br from-white to-gray-50">
          <div className="container mx-auto max-w-6xl">
            <ResponsiveHeading
              centered
              size="xl"
              subtext={`As ${regionData.description}, ${regionData.name} offers extensive security opportunities requiring CRJMC background checks.`}
            >
              {regionData.name} Security Guard Opportunities
            </ResponsiveHeading>
            <div className="grid md:grid-cols-2 gap-8">
              <ContentCard title="Key Employment Areas">
                <p className="mb-4">
                  {regionData.name}'s diverse security sector offers numerous
                  positions requiring valid <strong>CRJMC checks</strong>:
                </p>
                <ul className="list-disc pl-6 space-y-2 mb-4">
                  {regionData.securityOpportunities.map(
                    (opportunity, index) => (
                      <li key={index}>
                        <strong>{opportunity}</strong>
                      </li>
                    ),
                  )}
                </ul>
                <div className="bg-primary/5 p-4 rounded-lg">
                  <p className="text-sm">
                    <strong>Note:</strong> All security positions in{" "}
                    {regionData.name} require a valid <strong>CRJMC</strong> and
                    security guard license.
                  </p>
                </div>
              </ContentCard>

              <ContentCard title="Major Security Employers">
                <p className="mb-4">
                  {regionData.name} hosts several leading security companies
                  that require <strong>CRJMC</strong> for employment:
                </p>
                <ul className="list-disc pl-6 space-y-2">
                  {regionData.majorEmployers.map((employer, index) => (
                    <li key={index}>
                      <strong>{employer.name}</strong> - {employer.description}
                    </li>
                  ))}
                </ul>
              </ContentCard>
            </div>
          </div>
        </section>

        {/* Region Security Industry Stats */}
        <section className="py-8 px-4 bg-primary/5">
          <div className="container mx-auto max-w-6xl">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <StatCard
                icon={DollarSign}
                value="$18-30"
                label="Hourly Rate Range"
              />
              <StatCard
                icon={Users}
                value="5,000+"
                label="Security Jobs in Ontario"
              />
              <StatCard icon={Clock} value="24/7" label="Work Availability" />
              <StatCard
                icon={Shield}
                value="2 Years"
                label="License Validity"
              />
            </div>
          </div>
        </section>

        {/* Final CTA */}
        <section className="relative py-16 px-4">
          <div className="container mx-auto max-w-7xl">
            <div className="grid md:grid-cols-2 overflow-hidden rounded-lg shadow-lg">
              <div className="relative h-[400px] md:h-full">
                <picture className="object-cover translate-y-20">
                  <source
                    srcSet="/images/security-guard/security-guard-with-background-check-cleared-big.jpg"
                    type="image/jpeg"
                  />
                  <img
                    src="/images/security-guard/security-guard-with-background-check-cleared-big.jpg"
                    alt={`Get your CRJMC background check for ${regionData.name} security guard license`}
                    className="absolute inset-0 w-full h-full object-cover"
                  />
                </picture>
              </div>
              <div className="bg-gradient-to-br from-white to-primary/5 text-gray-800 p-12 md:p-16 lg:p-24 flex items-center">
                <div className="max-w-xl">
                  <ResponsiveHeading size="xl" className="text-primary-dark">
                    Get Your CRJMC in 15 Minutes
                  </ResponsiveHeading>
                  <p className="text-base sm:text-lg md:text-xl text-gray-700 mb-8">
                    Fast, reliable background checks for {regionData.name}{" "}
                    security guard licensing. Start your application now and get
                    results in minutes.
                  </p>
                  <div className="flex flex-col sm:flex-row items-center gap-4">
                    <ButtonCTA showSubheader={false} />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default RegionTemplate;

export const Head = ({ regionData }: RegionTemplateProps) => [
  <SEO
    key="seo"
    title={regionData.seoData.title}
    description={regionData.seoData.description}
    ogType="website"
    ogTitle={regionData.seoData.ogTitle}
    ogDescription={regionData.seoData.ogDescription}
    ogImage="/images/security-guard/security-guard-with-background-check-cleared-small.png"
  />,
  <script
    key="schema"
    type="application/ld+json"
    dangerouslySetInnerHTML={{
      __html: JSON.stringify({
        "@context": "https://schema.org",
        "@type": "WebPage",
        name: regionData.schemaData.name,
        description: regionData.schemaData.description,
        provider: {
          "@type": "Organization",
          name: "CRJMC",
          "@id": "https://crjmc.net",
          url: "https://crjmc.net",
          logo: {
            "@type": "ImageObject",
            url: "https://crjmc.net/images/crjmc-net-512.png",
          },
        },
        mainEntity: {
          "@type": "Service",
          name: `${regionData.name} Police Check & CRJMC Service`,
          description: `Online ${regionData.name} police check and criminal record check service. Faster alternative to ${regionData.name} Police Service with results in 15 minutes. Accepted for security guard licensing, employment screening, and volunteer positions.`,
          serviceType: "Criminal Record Check",
          image: {
            "@type": "ImageObject",
            url: "https://crjmc.net/images/security-guard/security-guard-with-background-check-cleared-big.jpg",
          },
          areaServed: regionData.schemaData.areaServed,
          aggregateRating: {
            "@type": "AggregateRating",
            ratingValue: 4.9,
            reviewCount: 257,
            bestRating: 5,
            worstRating: 3,
          },
          offers: [
            {
              "@type": "Offer",
              name: `${regionData.name} Police Check - Standard`,
              description: `Online criminal record check for ${regionData.name} - results in 15 minutes`,
              price: `${CRJMC_PRICE}`,
              priceCurrency: "CAD",
              availability: "https://schema.org/InStock",
              priceValidUntil: new Date(
                new Date().setFullYear(new Date().getFullYear() + 1),
              )
                .toISOString()
                .split("T")[0],
              url: `https://crjmc.net/ontario/${regionData.name.toLowerCase()}`,
            },
            {
              "@type": "Offer",
              name: `${regionData.name} CRJMC Check`,
              description: `Comprehensive criminal record and judicial matters check for ${regionData.name}`,
              price: `${CRJMC_PRICE}`,
              priceCurrency: "CAD",
              availability: "https://schema.org/InStock",
              priceValidUntil: new Date(
                new Date().setFullYear(new Date().getFullYear() + 1),
              )
                .toISOString()
                .split("T")[0],
              url: `https://crjmc.net/ontario/${regionData.name.toLowerCase()}`,
            },
          ],
          hasOfferCatalog: {
            "@type": "OfferCatalog",
            name: `${regionData.name} Police Check & CRJMC Services`,
            itemListElement: [
              {
                "@type": "OfferCatalog",
                name: "Criminal Record Checks",
                description: `Standard criminal record checks for ${regionData.name} residents and businesses`,
              },
              {
                "@type": "OfferCatalog",
                name: "CRJMC Background Checks",
                description: `Comprehensive criminal record and judicial matters checks for ${regionData.name}`,
              },
            ],
          },
        },
        breadcrumb: {
          "@type": "BreadcrumbList",
          itemListElement: [
            {
              "@type": "ListItem",
              position: 1,
              name: "Home",
              item: "https://crjmc.net",
            },
            {
              "@type": "ListItem",
              position: 2,
              name: "Ontario",
              item: "https://crjmc.net/ontario",
            },
            {
              "@type": "ListItem",
              position: 3,
              name: `${regionData.name} Police Check`,
              item: `https://crjmc.net/ontario/${regionData.name.toLowerCase()}`,
            },
          ],
        },
        keywords: `${regionData.name} police check, ${regionData.name} CRJMC, ${regionData.name} criminal record check, police background check ${regionData.name}, criminal record search ${regionData.name}`,
      }),
    }}
  />,
];
